import React, { createContext, useContext, useState, useEffect } from 'react';
import { EventType, InteractionRequiredAuthError } from '@azure/msal-browser';
import { msalInstance, loginRequest, tokenRequest } from './msalConfig';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [user, setUser] = useState(null);
    const [msalInitialized, setMsalInitialized] = useState(false);
    const [, setNeedsReauth] = useState(false);

    useEffect(() => {
        const initializeMsal = async () => {
            try {
                await msalInstance.initialize();
                
                // Handle redirect promise - important for popup flows
                await msalInstance.handleRedirectPromise();
                
                setMsalInitialized(true);

                // Check if user is already signed in
                const accounts = msalInstance.getAllAccounts();
                if (accounts.length > 0) {
                    msalInstance.setActiveAccount(accounts[0]);
                    setIsAuthenticated(true);
                    setUser(accounts[0]);
                }
            } catch (error) {
                console.error('MSAL initialization failed:', error);
            } finally {
                setIsLoading(false);
            }
        };

        initializeMsal();

        const callbackId = msalInstance.addEventCallback((event) => {
            if (event.eventType === EventType.LOGIN_SUCCESS) {
                setIsAuthenticated(true);
                setUser(event.payload.account);
            } else if (event.eventType === EventType.LOGOUT_SUCCESS) {
                setIsAuthenticated(false);
                setUser(null);
            }
        });

        return () => {
            if (callbackId) {
                msalInstance.removeEventCallback(callbackId);
            }
        };
    }, []);

    const login = async () => {
        if (!msalInitialized) {
            console.error('MSAL not initialized');
            return;
        }

        try {
            // First, try to get a token silently
            const account = msalInstance.getActiveAccount();
            if (account) {
                try {
                    await msalInstance.acquireTokenSilent({
                        ...tokenRequest,
                        account: account
                    });
                    setIsAuthenticated(true);
                    setUser(account);
                    return;
                } catch (error) {
                    if (!(error instanceof InteractionRequiredAuthError)) {
                        throw error;
                    }
                }
            }

            // If silent token acquisition fails, do interactive login
            const response = await msalInstance.loginPopup(loginRequest);
            if (response) {
                msalInstance.setActiveAccount(response.account);
                setIsAuthenticated(true);
                setUser(response.account);
            }
        } catch (error) {
            console.error('Login failed:', error);
            throw error;
        }
    };

    const logout = () => {
        if (!msalInitialized) {
            console.error('MSAL not initialized');
            return;
        }
        msalInstance.logoutPopup();
    };

    const getAccessToken = async () => {
        if (!msalInitialized) {
            throw new Error('MSAL not initialized');
        }

        try {
            const account = msalInstance.getActiveAccount();
            if (!account) {
                throw new Error('No active account found');
            }

            try {
                const response = await msalInstance.acquireTokenSilent({
                    ...tokenRequest,
                    account: account
                });
                return response.accessToken;
            } catch (error) {
                if (error instanceof InteractionRequiredAuthError) {
                    setNeedsReauth(true);
                    throw error;
                }
                throw error;
            }
        } catch (error) {
            console.error('Error getting access token:', error);
            throw error;
        }
    };

    if (isLoading) {
        return <div>Loading...</div>;
    }

    return (
        <AuthContext.Provider value={{
            isAuthenticated,
            user,
            login,
            logout,
            getAccessToken
        }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
